import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Clock, Award, Target } from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePWA';

interface NotificationPromptProps {
  show?: boolean;
  onDismiss?: () => void;
  onEnable?: () => void;
  compact?: boolean;
}

export const NotificationPrompt: React.FC<NotificationPromptProps> = ({
  show,
  onDismiss,
  onEnable,
  compact = false
}) => {
  const { permission, requestPermission } = usePushNotifications();
  const [requesting, setRequesting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  const shouldShow = show !== undefined ? show : permission === 'default' && !dismissed;

  const handleEnable = async () => {
    setRequesting(true);
    
    try {
      const result = await requestPermission();
      
      if (result === 'granted') {
        if (onEnable) {
          onEnable();
        }
        if (onDismiss) {
          onDismiss();
        }
      }
    } finally {
      setRequesting(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!shouldShow) {
    return null;
  }

  if (compact) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-50"
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                Stay on Track
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                Get reminders for meals and challenges
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleEnable}
                disabled={requesting}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requesting ? 'Enabling...' : 'Enable'}
              </button>
              
              <button
                onClick={handleDismiss}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleDismiss}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Stay on Track
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Enable notifications to get helpful reminders about your nutrition goals and challenges.
            </p>
          </div>

          {/* Notification types */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="font-medium text-sm text-gray-900 dark:text-white">
                  Meal Reminders
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Never miss logging your meals
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="font-medium text-sm text-gray-900 dark:text-white">
                  Goal Reminders
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Stay focused on your nutrition targets
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-sm text-gray-900 dark:text-white">
                  Achievement Alerts
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Celebrate your wins and milestones
                </p>
              </div>
            </div>
          </div>

          {/* Privacy note */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-6">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Privacy:</span> You can change notification preferences 
              anytime in your settings. We'll only send helpful, relevant notifications.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Not Now
            </button>
            
            <button
              onClick={handleEnable}
              disabled={requesting}
              className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Bell className="w-4 h-4" />
              {requesting ? 'Enabling...' : 'Enable Notifications'}
            </button>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationPrompt;